import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {ActionIcon, Group, Tooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AgentApprovalUpdateSource, CitationGenerationMode} from 'llm/chat-library/ChatDAO';
import {useAgentWorkspaceFolderName} from 'llm/chat-library/CoScientistAgentWorkspaceFolderChip';
import PlusMenu from 'llm/chat-library/PlusMenu';
import {
  getAgentTypeText,
  getAgentDescriptionForChatInput,
  getAvailableAgentTypes,
  getDefaultAgentType,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import AIButton from 'llm/chat-library/ui/AIButton';
import {CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX} from 'llm/chat-library/ui/AIStyleConstants';
import FullActionsInfoPopover from 'llm/chat-library/ui/FullActionsInfoPopover';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import isLikelyMobileCapabilities from 'util/isLikelyMobileCapabilities';

export function AgentToolbar({
  textareaRef,
  fileInputRef,
  cameraInputRef,
  allowedChatTypes,
  agentType,
  setAgentType,
  inputDisabled,
  publicDataEnabled,
  setPublicDataEnabled,
  citationGenerationMode,
  setCitationGenerationMode,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  fullAccessEnabled,
  fullAccessFolderId,
  onGrantFullAccess,
  onRevokeFullAccess,
  voiceSessionActive = false,
  onSelectSkill,
  useBackgroundAgentQueue,
  setUseBackgroundAgentQueue,
  restrictToAgentControls = false,
  hideCitationsControl = false,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  allowedChatTypes: Array<ChatType>;
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  citationGenerationMode: CitationGenerationMode;
  setCitationGenerationMode: (citationGenerationMode: CitationGenerationMode) => void;
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  fullAccessEnabled: boolean;
  /**
   * Names the folder that scopes Full actions for this chat. The chip's info popover always
   * renders; this only adds the folder-scoping sentence to it when set.
   */
  fullAccessFolderId?: string | null;
  onGrantFullAccess?: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  onRevokeFullAccess?: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  cameraInputRef: React.RefObject<HTMLInputElement>;
  inputDisabled: boolean;
  voiceSessionActive?: boolean;
  onSelectSkill?: (skillName: string) => void;
  useBackgroundAgentQueue?: boolean;
  setUseBackgroundAgentQueue?: (enabled: boolean) => void;
  /**
   * When true, restricts the toolbar to controls that apply to messaging a saved agent (routine)
   * directly: the agent-type chips and the Full-actions chip are hidden, and the Mode section is
   * hidden in the plus menu. Files, web search, skills, and connectors remain available.
   */
  restrictToAgentControls?: boolean;
  /** Hides Citations when the caller cannot apply its setting to the submitted message. */
  hideCitationsControl?: boolean;
}): JSX.Element {
  const [isRevokingFullAccess, setIsRevokingFullAccess] = useState(false);
  // Only queries when fullAccessFolderId is set (see useAgentWorkspaceFolderName / skip).
  const fullAccessFolderName = useAgentWorkspaceFolderName(fullAccessFolderId ?? null);
  const handleCameraButtonClick = useCallback((): void => {
    if (!inputDisabled && cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  }, [inputDisabled, cameraInputRef]);

  const handleRevokeFullAccess = useCallback(async (): Promise<void> => {
    if (onRevokeFullAccess == null) {
      return;
    }
    setIsRevokingFullAccess(true);
    try {
      await onRevokeFullAccess(AgentApprovalUpdateSource.FULL_ACTIONS_CHIP);
    } finally {
      setIsRevokingFullAccess(false);
    }
  }, [onRevokeFullAccess]);

  const plusMenu = (
    <PlusMenu
      fileInputRef={fileInputRef}
      agentType={agentType}
      availableAgentTypes={getAvailableAgentTypes(allowedChatTypes)}
      setAgentType={restrictToAgentControls ? undefined : setAgentType}
      inputDisabled={inputDisabled}
      voiceSessionActive={voiceSessionActive}
      publicDataEnabled={publicDataEnabled}
      setPublicDataEnabled={setPublicDataEnabled}
      citationGenerationMode={citationGenerationMode}
      setCitationGenerationMode={setCitationGenerationMode}
      selectedMcpServerIds={selectedMcpServerIds}
      setSelectedMcpServerIds={setSelectedMcpServerIds}
      fullAccessEnabled={fullAccessEnabled}
      onGrantFullAccess={onGrantFullAccess}
      onRevokeFullAccess={onRevokeFullAccess}
      onSelectSkill={onSelectSkill}
      useBackgroundAgentQueue={useBackgroundAgentQueue}
      setUseBackgroundAgentQueue={setUseBackgroundAgentQueue}
      restrictToAgentControls={restrictToAgentControls}
      hideCitationsControl={hideCitationsControl}
    />
  );

  return (
    <div
      {...getTestProps({component: 'AgentToolbar', element: 'root'})}
      className={css(styles.agentTypeButtons)}
      onMouseDown={(e) => {
        // Keep toolbar interactions from moving focus inside the chat input group.
        // This ensures :focus-within styles activate only when the textarea itself is focused.
        e.preventDefault();
      }}
    >
      {plusMenu}
      {/* Show a spinner while settings are loading (only possible for existing chats) */}
      {!restrictToAgentControls &&
        !voiceSessionActive &&
        (agentType == null ? (
          <Spinner aria-label="Loading agent settings" />
        ) : (
          (() => {
            const availableAgentTypes = getAvailableAgentTypes(allowedChatTypes);
            const defaultAgentType = getDefaultAgentType(null, allowedChatTypes);
            const isNonDefaultAgent = agentType !== defaultAgentType;
            const isAssistantEnabledForChat = availableAgentTypes.includes(AgentType.ASSISTANT);

            return availableAgentTypes.map((option: AgentType) => {
              // Assistant is implicitly enabled and never shown as a chip.
              if (option === AgentType.ASSISTANT) {
                return null;
              }

              // In Assistant-enabled chats, only render the currently selected non-Assistant chip.
              if (
                option !== agentType &&
                availableAgentTypes.length > 1 &&
                (isAssistantEnabledForChat || isNonDefaultAgent)
              ) {
                return null;
              }

              const showCloseIcon =
                isNonDefaultAgent && availableAgentTypes.length > 1 && option === agentType;
              const toggleButton = isAssistantEnabledForChat ? (
                <UnsetButton
                  key={option}
                  onClick={() => {
                    setAgentType(defaultAgentType);
                  }}
                  text={getAgentTypeText(option)}
                  disabled={inputDisabled}
                  testKey={option}
                />
              ) : (
                <ToggleButton
                  key={option}
                  agentType={option}
                  isActive={option === agentType}
                  showCloseIcon={showCloseIcon}
                  onClick={() => {
                    if (option === agentType) {
                      if (!showCloseIcon) {
                        return;
                      }
                      setAgentType(defaultAgentType);
                      return;
                    }
                    setAgentType(option);
                  }}
                  text={getAgentTypeText(option)}
                  disabled={inputDisabled}
                  testKey={option}
                />
              );
              return toggleButton;
            });
          })()
        ))}
      {!restrictToAgentControls && BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS && fullAccessEnabled && (
        <FullActionsInfoPopover folderName={fullAccessFolderId != null ? fullAccessFolderName : null}>
          <span>
            <UnsetButton
              onClick={handleRevokeFullAccess}
              text="Full actions"
              disabled={inputDisabled || isRevokingFullAccess}
              testKey="fullAccess"
            />
          </span>
        </FullActionsInfoPopover>
      )}
      {!voiceSessionActive && isLikelyMobileCapabilities() && (
        <AIButton
          ariaLabel="Take and upload photo"
          disabled={inputDisabled}
          icon="snapshot"
          onClick={handleCameraButtonClick}
        />
      )}
    </div>
  );
}

/**
 * A toggle button used to pick an agent or an agent feature like public data.
 */
function ToggleButton(props: {
  agentType: AgentType;
  isActive: boolean;
  text: React.ReactNode;
  showCloseIcon: boolean;
  onClick: () => void;
  disabled: boolean;
  testKey?: string;
}): JSX.Element {
  const {agentType, isActive, text, showCloseIcon, onClick, disabled, testKey} = props;
  const tooltipText = getAgentDescriptionForChatInput(agentType);
  const button = (
    // NB: I did not see an out of the box toggle button in Mantine v6.
    <Button
      aria-pressed={isActive}
      className={classNames({'is-active': isActive}, css(styles.agentTypeButton))}
      size={ButtonSize.MINI}
      buttonStyle={isActive ? ButtonStyle.TERTIARY_BLUE : ButtonStyle.TERTIARY_WHITE}
      onClick={onClick}
      textClassName={css(styles.agentTypeText)}
      text={
        <>
          {text}
          {showCloseIcon && <BenchlingIcon className={css(styles.closeIcon)} icon="close" />}
        </>
      }
      disabled={disabled}
      dataTestAttrs={testKey ? {component: 'ChatInput', element: 'agentToggle', key: testKey} : undefined}
    />
  );
  if (tooltipText == null) {
    return button;
  }
  return (
    <Tooltip label={tooltipText} position="top" openDelay={0} closeDelay={0} withinPortal={true}>
      <span>{button}</span>
    </Tooltip>
  );
}

/**
 * A pill showing a currently-active choice (the selected non-default agent, or the Full actions
 * chip) that can only be dismissed via its own close button -- not by clicking anywhere on the
 * pill -- mirroring the removable-chip pattern used for attachments in ChatInput.
 */
function UnsetButton(props: {
  text: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  testKey?: string;
}): JSX.Element {
  const {text, onClick, disabled, testKey} = props;
  const removeLabel = typeof text === 'string' ? `Remove ${text}` : 'Remove';
  return (
    <Group
      className={css(styles.unsetPill, disabled && styles.unsetPillDisabled)}
      noWrap={true}
      align="center"
      role="group"
      aria-label={typeof text === 'string' ? text : undefined}
      spacing={4}
      {...getTestProps(testKey ? {component: 'ChatInput', element: 'agentToggle', key: testKey} : undefined)}
    >
      <span className={css(styles.unsetPillLabel)}>{text}</span>
      <ActionIcon
        aria-label={removeLabel}
        className={css(styles.unsetPillRemove)}
        disabled={disabled}
        onClick={onClick}
        size="xs"
        variant="transparent"
      >
        <BenchlingIcon icon="close" />
      </ActionIcon>
    </Group>
  );
}

const styles = StyleSheet.create({
  agentTypeButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  agentTypeButton: {
    fontWeight: 400,
    fontSize: '13px',
    padding: '4px 10px 5px',
  },
  closeIcon: {
    marginLeft: '6px',
    marginRight: 0, // button has padding.
    opacity: 0.5,
  },
  agentTypeText: {
    flex: 'none',
  },
  unsetPill: {
    // Fixed (not min) height, exactly matching sibling toolbar controls: a min-height here would
    // let the label's natural line-height push the pill a pixel or two taller, which shifted the
    // whole toolbar (and the plus menu) whenever the chip appeared.
    height: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    boxSizing: 'border-box',
    padding: '0 6px 0 10px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 400,
    backgroundColor: Color.BLUE_100,
    border: `1px solid ${Color.WHITE}`,
    color: Color.BLUE_450,
  },
  unsetPillDisabled: {
    backgroundColor: Color.GRAY_50,
    color: Color.GRAY_500,
  },
  unsetPillLabel: {
    lineHeight: 'normal',
  },
  unsetPillRemove: {
    color: 'inherit',
    opacity: 0.5,
    ':hover': {
      opacity: 1,
      backgroundColor: 'transparent',
    },
  },
});
